import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { GET_PRODUCTION_DECLARATION_MATERIALS_KEY } from '@/keys';
import { useUpdateProductionDeclarationCorrectionRequestResolution } from '@/services/apis/mutations/production-declaration/use-update-production-declaration-correction-request-resolution';
import type { CorrectionRequest } from '@/types/core/purchase-order/correction-request';

type UseProductionDeclarationCorrectionRequestCommentResolutionParams = {
  commentsQueryKey: QueryKey;
  commodityOriginId: string;
  materialId: string;
  productionDeclarationId: string;
};

export function useProductionDeclarationCorrectionRequestCommentResolution({
  commentsQueryKey,
  commodityOriginId,
  materialId,
  productionDeclarationId,
}: UseProductionDeclarationCorrectionRequestCommentResolutionParams) {
  const queryClient = useQueryClient();
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });
  const { t: tCommon } = useTranslation('common', {
    useSuspense: false,
  });
  const {
    isPending: isUpdatingCommentResolution,
    mutate: updateCorrectionRequestResolution,
  } = useUpdateProductionDeclarationCorrectionRequestResolution();
  const handleCommentResolutionChange = (comment: CorrectionRequest) => {
    updateCorrectionRequestResolution(
      {
        productionDeclarationId,
        materialId,
        commodityOriginId,
        correctionRequestId: comment.id,
        isResolved: !comment.isResolved,
      },
      {
        onSuccess: async (updatedComment) => {
          queryClient.setQueryData<CorrectionRequest[]>(
            commentsQueryKey,
            (currentComments = []) =>
              currentComments.map((currentComment) =>
                currentComment.id === updatedComment.id
                  ? updatedComment
                  : currentComment,
              ),
          );
          await queryClient.invalidateQueries({
            queryKey: [
              ...GET_PRODUCTION_DECLARATION_MATERIALS_KEY,
              productionDeclarationId,
            ],
          });
        },
        onError: () => {
          toast.error(tCommon('error'), {
            description: t(
              'materialCommodityOrigins.comments.updateResolutionError',
            ),
          });
        },
      },
    );
  };

  return {
    handleCommentResolutionChange,
    isUpdatingCommentResolution,
  };
}
